import { useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, call } from '@/api/client'
import type { components } from '@/api/schema'
import { useMutation, useQuery } from '@/api/query'
import { branded, useBrand } from '@/brand/context'
import { downloadCsv } from '@/lib/csv'
import { formatDate } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { findingsCsv } from './findingsCsv'

type FindingGroup = components['schemas']['FindingGroupOut']
type Finding = components['schemas']['FindingOut']
type AuditRun = components['schemas']['AuditRunOut']
type Proposal = components['schemas']['TechnicalProposalOut']

/**
 * The technical audit — S-D5's audit screen, on real findings (S-B23).
 *
 * **Organised by who acts, not by severity**, and the design's own note is the
 * argument: *"a list sorted by severity buries the four items the tenant actually
 * has to do"*. So the page is two cards. The first is what needs a human at the
 * organisation, open. The second is what AugRank can fix, collapsed.
 *
 * **The second card says "AugRank can fix these", not "fixed automatically".**
 * S-D5's mock says the latter because it is drawn for the finished product; the
 * pipeline that writes those fixes is S-B25, the gate is S-B26 and the
 * publishers are S-B28, which has not landed. Until it does, claiming a fix
 * happened would be the one kind of wrong an audit screen must never be.
 *
 * Its note points at `/seo/approvals` since S-B27, which is the release that
 * made the destination exist — before it, the same sentence said the queue
 * "arrives in a later release", and leaving that in place after it shipped
 * would have been the same lie in the other direction.
 *
 * **Nothing here knows what a finding code means.** The label, the severity, the
 * remedy and the count all come off the API, because `app/seo/findings.CATALOGUE`
 * is where the actor split is decided — a copy of it in TypeScript would be the
 * hand-written DTO REQUIREMENTS §9.1 forbids, and it would be the copy that
 * disagreed about which column something belongs in.
 */
export function TechnicalAudit(): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  // SEO-UI-8. `?site=` opens this screen on the card that linked here, which is
  // what makes the *Sites* card's *Run audit now* a per-site action rather than
  // a link to whichever site happens to be first. `RunTriggers` reads the same
  // parameter the same way, and `ReasoningTrail` reads `?run=`. An unknown id
  // falls through to the first site rather than erroring: the picker is on
  // screen, and a stale bookmark is not a failure.
  const [params] = useSearchParams()
  const asked = params.get('site')
  const [siteId, setSiteId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const requested = listed.some((row) => row.id === asked) ? asked : null
  const selectedId = siteId ?? requested ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null

  const audit = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/audit', {
              params: { path: { site_id: selectedId } },
              signal,
            }),
          ),
    [selectedId],
  )

  const start = useMutation((id: string) =>
    call(() =>
      api.POST('/api/v1/seo/sites/{site_id}/audit', { params: { path: { site_id: id } } }),
    ),
  )

  const data = audit.state.data
  const running = data?.running ?? false

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">Technical audit</h1>
        <span className="app-spacer" />
        {/* Always drawn, one site or several (parity P-AUD-01): the control is
            what says which site the numbers below are about. */}
        {listed.length > 0 ? (
          <select
            className="ar-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
        {site !== null ? (
          <Button
            // SEO-UI-42. `seo.html:219` draws this action at `--sm` (2rem). The
            // default is `--ar-control-md` at 2.625rem, which beside an `ar-h1`
            // reads as the screen's primary action rather than as the header's
            // one control.
            size="sm"
            disabled={running || start.pending}
            onClick={() => {
              void start.run(site.id).then((result) => {
                if (!result.ok) return
                audit.refetch()
                toast.show({
                  kind: 'success',
                  title: result.data.queued ? 'Audit queued' : 'An audit is already running',
                  body: result.data.queued
                    ? `${brand.productName} will crawl ${site.domain} and report back in a few minutes.`
                    : `${brand.productName} is already crawling ${site.domain}. This will finish shortly.`,
                })
              })
            }}
          >
            {running ? 'Audit running…' : 'Run audit now'}
          </Button>
        ) : null}
      </div>

      {start.state.status === 'error' ? <InlineError failure={start.state.failure} /> : null}

      {sites.state.status === 'loading' ? (
        <Loading label="Loading your sites" />
      ) : sites.state.status === 'error' ? (
        <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
      ) : site === null ? (
        <Empty
          title="No site yet"
          body={`Add your website and ${brand.productName} can check it for the things that stop Google indexing it.`}
          action={<Link to="/onboarding/site">Add your site</Link>}
        />
      ) : audit.state.status === 'loading' ? (
        <Loading label="Loading the audit" />
      ) : audit.state.status === 'error' ? (
        <ErrorState failure={audit.state.failure} onRetry={audit.refetch} />
      ) : data === null || data.run === null || data.run === undefined ? (
        <Empty
          title="No audit yet"
          body={`${brand.productName} has not checked ${site.domain}. Running one takes a couple of minutes and makes about two hundred requests to your server.`}
        />
      ) : (
        <div className="ar-stack">
          <Stats
            score={data.health_score ?? null}
            needsYou={countOf(data.needs_you)}
            canFix={countOf(data.we_can_fix)}
            brokenLinks={brokenLinksOf(data.run)}
          />
          <Summary run={data.run} />
          <Column
            heading="Needs a decision from you"
            tone="warning"
            groups={data.needs_you}
            empty="Nothing on this site needs you right now."
            // The list a developer needs, as a file: every finding in this
            // column with its page, its severity and the evidence — the broken
            // link's target and status, the pages that link to it — so the
            // tenant forwards a spreadsheet rather than a screenshot.
            action={
              countOf(data.needs_you) > 0 ? (
                <Button
                  tone="ghost"
                  size="sm"
                  onClick={() => {
                    downloadCsv(
                      `${site.domain}-audit-findings.csv`,
                      findingsCsv(data.needs_you, site.domain),
                    )
                  }}
                >
                  Download as CSV
                </Button>
              ) : null
            }
          />
          <Column
            heading={`${brand.productName} can fix these`}
            tone="success"
            groups={data.we_can_fix}
            // A template literal, not a string with braces in it: this printed
            // the placeholder verbatim on the live tenant (2026-09-19).
            empty={`Nothing here for ${brand.productName} to fix.`}
            note={
              <>
                These are changes {brand.productName} is able to make on its own — adding a tag,
                describing an image, publishing a sitemap.{' '}
                {/* "Nothing has been changed yet, and nothing will be without
                    your say-so" is true while no proposal exists or one is
                    waiting; beside *approved automatically on 10 Sept* it was a
                    contradiction on the live tenant. Said only when it is true. */}
                {data.proposal === null ||
                data.proposal === undefined ||
                data.proposal.state === 'pending' ||
                data.proposal.state === 'rejected'
                  ? 'Nothing has been changed yet, and nothing will be without your say-so. '
                  : ''}
                <ProposalLine proposal={data.proposal ?? null} />
              </>
            }
          />
          <RecentlyFixed groups={data.recently_fixed} />
        </div>
      )}
    </>
  )
}

/**
 * What the run reached, and the caveats that stop the four figures being read
 * as more than they are.
 *
 * **The four figures left this card in SEO-UI-35…39** and are a top-level
 * `ar-grid--4` above it now — see `Stats`. What stays is everything the
 * prototype has no cell for: which run these are, whether it finished, and the
 * three sentences that qualify them.
 *
 * A `partial` run is the one that needs saying out loud: the audit produced
 * findings and something it wanted — almost always PageSpeed Insights — did not
 * answer. A screen that rendered that identically to a clean run would be
 * reporting an absent measurement as a passing one.
 */
function Summary({ run }: { run: AuditRun }): JSX.Element {
  const summary = (run.summary ?? {}) as Record<string, unknown>
  const pages = typeof summary.pages_crawled === 'number' ? summary.pages_crawled : 0
  const bounded = (summary.bounded ?? {}) as Record<string, unknown>

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>
          {pages.toLocaleString('en-IN')} page{pages === 1 ? '' : 's'} checked
        </h2>
        <span className="ar-card__spacer" />
        {run.status === 'queued' || run.status === 'running' ? (
          <span className="ar-badge">Running</span>
        ) : run.status === 'partial' ? (
          <span className="ar-badge ar-badge--warning">Partly measured</span>
        ) : run.status === 'failed' ? (
          <span className="ar-badge ar-badge--danger">Failed</span>
        ) : run.finished_at !== null && run.finished_at !== undefined ? (
          <span className="ar-text-body-sm ar-muted">{formatDate(run.finished_at)}</span>
        ) : null}
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
          The health score is a summary of what is open, not a measurement — it moves when something
          is fixed and it is not comparable with another site&rsquo;s.
        </p>
        {run.status === 'partial' ? (
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            {describeDegradation(summary)}
          </p>
        ) : null}
        {bounded.page_limit_reached === true ? (
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            The site has more pages than one audit covers, so this is a sample rather than a
            complete sweep. The pages your sitemap lists were checked first.
          </p>
        ) : null}
      </div>
    </section>
  )
}

/** How many findings a column holds, across its groups. */
function countOf(groups: FindingGroup[]): number {
  return groups.reduce((total, group) => total + group.count, 0)
}

/**
 * The run's broken-link tally, off `runs.summary`.
 *
 * Read from the summary rather than counted out of `needs_you`, because they are
 * different numbers and the tile wants this one: the crawler counts every broken
 * link it followed, and the finding column groups them per target URL, so a
 * homepage nav linking to one dead page from forty pages is forty links and one
 * finding. `seo.html:297` heads the tile *Broken links*.
 */
function brokenLinksOf(run: AuditRun): number {
  const summary = (run.summary ?? {}) as Record<string, unknown>
  return typeof summary.broken_links === 'number' ? summary.broken_links : 0
}

/** What a `partial` run could not measure, in the tenant's terms. */
function describeDegradation(summary: Record<string, unknown>): string {
  const unavailable = (summary.pagespeed_unavailable ?? {}) as Record<string, unknown>
  const strategies = Object.keys(unavailable)
  if (strategies.length > 0) {
    return (
      `Google\u2019s PageSpeed service did not answer for ${strategies.join(' and ')}, so the ` +
      'speed measurements are missing from this run. Everything else was checked.'
    )
  }
  const bounded = (summary.bounded ?? {}) as Record<string, unknown>
  if (bounded.out_of_time === true) {
    return 'The audit ran out of its time budget before it finished, so this covers part of the site. Everything it did check is below.'
  }
  return 'Part of this audit could not be completed. Everything that was checked is below.'
}

/**
 * What became of the fixable findings, in one sentence that is true.
 *
 * The note used to say *"they are proposed together as one item in the approval
 * queue"* whatever had happened, and on the live tenant it said it beside a
 * queue reading *Nothing is waiting* (walkthrough 2026-09-12). The API now
 * returns the row the run filed, so each state gets its own sentence — and the
 * absence of a row is said as an absence, not as a proposal.
 */
function ProposalLine({ proposal }: { proposal: Proposal | null }): JSX.Element {
  const brand = useBrand()
  if (proposal === null) {
    return (
      <>
        No proposal has been filed for them yet — the next audit run files one, as one item in the{' '}
        <Link to="/seo/approvals">approval queue</Link>.
      </>
    )
  }
  const bundle = `${String(proposal.actions)} ${proposal.actions === 1 ? 'fix' : 'fixes'}`
  const when = proposal.decided_at ? ` on ${formatDate(proposal.decided_at)}` : ''
  switch (proposal.state) {
    case 'pending':
      return (
        <>
          They are proposed together as one item of {bundle} in the{' '}
          <Link to="/seo/approvals">approval queue</Link>, waiting for your decision since{' '}
          {formatDate(proposal.created_at)}.
        </>
      )
    case 'auto_approved':
      return (
        <>
          Your <Link to="/seo/approvals">approval settings</Link> let {brand.productName} make
          technical fixes without asking, so the item of {bundle} was approved automatically{when}.
        </>
      )
    case 'approved':
      return (
        <>
          The item of {bundle} was approved{when}; what is still listed here is what the last crawl
          found open.
        </>
      )
    case 'rejected':
      return (
        <>
          The item of {bundle} was declined{when}. A later crawl that finds something different asks
          again.
        </>
      )
    default:
      return (
        <>
          They are proposed together as one item in the{' '}
          <Link to="/seo/approvals">approval queue</Link>.
        </>
      )
  }
}

/**
 * One of the screen's two columns: findings grouped by kind, worst first.
 *
 * **SEO-UI-40 — the rows are `.app-conn` now, and SF-0 decided that.** They were
 * a bare `<details>/<summary>` per group, which renders the user agent's own
 * disclosure triangle, its own spacing and its own type inside an otherwise
 * tokenised card — different in every browser and ignoring the scale entirely.
 * `seo.html:304-317` draws each finding as a `.sc-conn` flex row with a
 * tone-tinted 2.75rem badge, and `app.css` has carried that as `.app-conn` since
 * the connections panel was built. The disclosure behaviour is kept: a group
 * still opens and closes, because a crawl that found sixty broken links must not
 * put sixty rows on the screen unasked.
 *
 * S-D5 draws the agent column collapsed and the tenant column open, and `open`
 * on the first group of the tenant column is what does that without a click.
 *
 * **No per-finding *Fix all* or *Allow /blog/* button** (SEO-UI-12), and this is
 * the design deciding rather than the session deferring. `app/seo/findings.py`
 * makes `actor` a property of the *code*, fixed in `CATALOGUE`: a finding in
 * this column is one AugRank has decided a human must act on, so a button here
 * that queued a fix would contradict the column it sits in. The two the
 * prototype draws are the two the catalogue is most explicit about — a broken
 * link "might want a redirect or might want the link removed", and every
 * `robots_*` code is `Actor.TENANT` because "a robot that edits robots.txt has
 * made the file meaningless". The one-press path for what AugRank *can* fix
 * exists and is the approval queue, which the other column's note links to.
 */
function Column({
  heading,
  tone,
  groups,
  empty,
  note,
  action,
}: {
  heading: string
  tone: 'warning' | 'success'
  groups: FindingGroup[]
  empty: string
  /** A `ReactNode` since S-B27, so the note can link to the approval queue. */
  note?: ReactNode
  /** Something to do with the whole column, in the header beside the count. */
  action?: ReactNode
}): JSX.Element {
  const brand = useBrand()
  const total = groups.reduce((sum, group) => sum + group.count, 0)
  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>{heading}</h2>
        <span className="ar-card__spacer" />
        {action}
        {/* The bare count, as `seo.html#audit` draws it (parity P-AUD-03); each
            group's own line says how many items it holds. */}
        <span className={`ar-badge ar-badge--${tone}`}>{total}</span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {note !== undefined ? (
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            {note}
          </p>
        ) : null}
        {groups.length === 0 ? (
          <p className="ar-empty ar-empty--inline">{empty}</p>
        ) : (
          groups.map((group, index) => (
            <details
              className="app-conn app-audit__group"
              key={group.code}
              open={tone === 'warning' && index === 0}
            >
              <summary className="app-audit__summary">
                <span className={`app-conn__logo app-audit__mark--${tone}`} aria-hidden="true">
                  {tone === 'warning' ? '⚠' : '✓'}
                </span>
                <span className="app-conn__body">
                  <strong>{group.label}</strong>
                  <span>
                    {group.count} {group.count === 1 ? 'item' : 'items'} ·{' '}
                    {severityLabel(group.severity)}
                  </span>
                </span>
              </summary>
              <div className="ar-stack ar-stack--tight app-audit__detail">
                <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
                  {branded(group.findings[0]?.remedy ?? '', brand)}
                </p>
                <ul className="ar-stack ar-stack--tight" style={{ margin: 0 }}>
                  {group.findings.map((finding) => (
                    <FindingRow key={finding.id} finding={finding} />
                  ))}
                </ul>
              </div>
            </details>
          ))
        )}
      </div>
    </section>
  )
}

/**
 * One finding: where it is, and the evidence that makes it checkable by hand.
 *
 * The evidence is the part that matters. *"Broken internal link"* is an
 * assertion; *"/gallery/2019-summer returned 404, linked from 3 pages"* is
 * something a tenant can open in a browser and confirm in ten seconds, which is
 * the difference between an audit that gets acted on and one that gets ignored.
 */
function FindingRow({ finding }: { finding: Finding }): JSX.Element {
  return (
    <li className="ar-text-body-sm">
      {finding.url !== null && finding.url !== undefined ? (
        <a className="ar-link ar-mono" href={finding.url} target="_blank" rel="noreferrer noopener">
          {pathOf(finding.url)}
        </a>
      ) : (
        <span>{finding.title}</span>
      )}
      <span className="ar-muted"> {evidenceOf(finding)}</span>
    </li>
  )
}

/**
 * The one line of evidence a finding carries, per shape of `detail`.
 *
 * Reading a handful of well-known keys rather than dumping the object: `detail`
 * is JSONB whose shape differs per code, and a rendered blob is something a
 * developer reads and a business owner does not. Anything unrecognised falls
 * through to nothing, which is why every branch is a lookup and none is a cast.
 */
function evidenceOf(finding: Finding): string {
  const detail = (finding.detail ?? {}) as Record<string, unknown>
  const parts: string[] = []

  const status = numberAt(detail, 'status')
  if (status !== null && status > 0) parts.push(`HTTP ${status.toFixed(0)}`)

  const referrers = numberAt(detail, 'linked_from_count')
  if (referrers !== null) {
    parts.push(`linked from ${referrers.toFixed(0)} page${referrers === 1 ? '' : 's'}`)
  }

  const withoutAlt = numberAt(detail, 'without_alt')
  const images = numberAt(detail, 'images')
  if (withoutAlt !== null) {
    parts.push(`${withoutAlt.toFixed(0)} of ${images === null ? '?' : images.toFixed(0)} images`)
  }

  const value = numberAt(detail, 'value')
  const metric = stringAt(detail, 'metric')
  if (value !== null && metric !== null) {
    // "4,452 ms on mobile (lab test)" read as jargon to the first tenant who
    // saw it. Seconds to one place is what a person compares against their
    // own sense of "slow", and the source is spelled out rather than named:
    // "measured from real visitors" or "simulated test". CLS has no unit and
    // stays at two places, which is how Google prints it.
    const source =
      stringAt(detail, 'source') === 'field' ? 'measured from real visitors' : 'simulated test'
    const strategy = stringAt(detail, 'strategy')
    const where =
      strategy === 'mobile' ? 'on a phone' : strategy === 'desktop' ? 'on a computer' : ''
    const shown = metric === 'cls' ? value.toFixed(2) : `${(value / 1000).toFixed(1)} seconds`
    parts.push(`${shown}${where === '' ? '' : ` ${where}`}, ${source}`)
  }

  const rule = stringAt(detail, 'rule')
  if (rule !== null && rule !== '') parts.push(`rule \u201C${rule}\u201D`)

  const hops = numberAt(detail, 'hops')
  if (hops !== null) parts.push(`${hops.toFixed(0)} redirects`)

  const reason = stringAt(detail, 'reason')
  if (reason !== null && reason !== '') parts.push(reason)

  // `internal_link_opportunity`: the to-do itself, since the title only says
  // which page. "link “sliding wardrobes” to /wardrobes" is what the person
  // opening the page needs, and the phrase is the one the page already uses.
  for (const suggestion of suggestionsOf(detail)) {
    parts.push(`link \u201C${suggestion.anchor}\u201D to ${pathOf(suggestion.url)}`)
  }

  return parts.length > 0 ? `\u2014 ${parts.join(', ')}` : ''
}

/**
 * The `suggestions` list an internal-link finding carries, or nothing.
 *
 * Each entry is checked field by field for the reason `numberAt` exists: the
 * shape is the API's to change, and a malformed entry renders as nothing
 * rather than as `undefined` in a sentence.
 */
function suggestionsOf(detail: Record<string, unknown>): { url: string; anchor: string }[] {
  const raw = detail['suggestions']
  if (!Array.isArray(raw)) return []
  const out: { url: string; anchor: string }[] = []
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue
    const record = entry as Record<string, unknown>
    if (typeof record['url'] === 'string' && typeof record['anchor'] === 'string') {
      out.push({ url: record['url'], anchor: record['anchor'] })
    }
  }
  return out
}

/**
 * One key of a JSONB blob, if it is the type this screen expects.
 *
 * `detail`'s shape differs per finding code, so every read is a check rather
 * than a cast — a `number` key that arrives as a string from a future revision
 * renders as nothing rather than as `[object Object]`.
 */
function numberAt(detail: Record<string, unknown>, key: string): number | null {
  const value = detail[key]
  return typeof value === 'number' ? value : null
}

function stringAt(detail: Record<string, unknown>, key: string): string | null {
  const value = detail[key]
  return typeof value === 'string' ? value : null
}

/**
 * What the last run found to be gone. The half of a trend a tenant reads first.
 *
 * **Grouped by the server, with the run's true tally** (SEO-UI-13), which is the
 * first two columns of `seo.html:324-335`. It listed one line per occurrence, so
 * a run that resolved sixty-four missing `alt` attributes printed sixty-four
 * near-identical sentences — the same reason the two columns above group.
 *
 * The grouping is `app/v1/seo_audit.py`'s and not this component's, and that is
 * the fix rather than the tidy-up: `recently_fixed` is a **sample**, so counting
 * the array here reported *20* for a run that resolved sixty-four and presented
 * it as the total. `FindingGroupOut.count` is the whole tally because the server
 * groups before it samples — the contract the two open columns already held.
 * The heading is the catalogue's label for the same reason it is there:
 * `finding.title` carries one occurrence's path.
 *
 * **One occurrence travels with each group** (`RECENTLY_FIXED_SAMPLE`), and this
 * card renders none of them: it reads a single `url` to tell a page-level code
 * from a site-level one, which is a property of the code.
 *
 * **No *What we did* column and no *Undo*, and that is not a deferral.** The
 * prototype's table is headed *"Fixed automatically"* and both of those columns
 * follow from it: you can describe an action and reverse it only if you took
 * one. This card reports something different and says so in its heading —
 * findings that were open at the last audit and are **absent from this one**.
 * `app/db/seo_audit.reconcile` closes them by observing they no longer occur,
 * and the tenant's own developer deploying a fix on Tuesday is by far the
 * commonest way that happens. "What we did: —" against work somebody else did
 * is a claim AugRank must not make, and an Undo button that offered to reinstate
 * a broken link it never broke would be worse. When S-B28's publishers apply a
 * fix, the record of what was applied is the approval queue's and an undo
 * belongs there, beside the thing that did it.
 *
 * Absent rather than empty when there is nothing: a card headed *"Fixed since
 * the last run"* with nothing under it reads as a reproach on a first run.
 */
function RecentlyFixed({ groups }: { groups: FindingGroup[] }): JSX.Element {
  const total = groups.reduce((sum, group) => sum + group.count, 0)
  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Fixed since the last audit</h2>
        <span className="ar-card__spacer" />
        <span className="ar-badge ar-badge--success">✓ {total}</span>
      </div>
      <div className="ar-table-wrap" tabIndex={0}>
        <table className="ar-table">
          <thead>
            <tr>
              <th scope="col">Issue</th>
              <th scope="col" className="ar-num">
                Pages
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Drawn even when empty (parity P-AUD-05): the card is part of the
                screen, and a first run says so in its one row. */}
            {groups.length === 0 ? (
              <tr>
                <td className="ar-muted" colSpan={2}>
                  Nothing fixed since the last audit yet.
                </td>
              </tr>
            ) : null}
            {groups.map((group) => (
              <tr key={group.code}>
                <td>{group.label}</td>
                {/* A site-level finding is not about a page, so its count is a
                    dash — `seo.html:330` draws exactly that for *No sitemap.xml*.
                    Read off the sample's first occurrence, because whether a code
                    names a URL is a property of the code and not of the run. */}
                <td className="ar-num">{sitewide(group) ? '—' : group.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

/** Whether this code is about the site rather than about pages of it. */
function sitewide(group: FindingGroup): boolean {
  const first = group.findings[0]
  return first !== undefined && (first.url === null || first.url === undefined)
}

function severityLabel(severity: string): string {
  if (severity === 'critical') return 'Critical'
  if (severity === 'high') return 'Important'
  if (severity === 'medium') return 'Worth fixing'
  return 'Minor'
}

/** A URL as its path, because every row on the screen is about the same site. */
function pathOf(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return url
  }
}

/**
 * One headline figure. SEO-UI-35, -36, -37, -38.
 *
 * The tone is a class rather than an inline `style={{ color: … }}`: the mockup
 * writes `style="color:var(--ar-success)"` because it is a static page with no
 * stylesheet of its own to add to, and `app.css` does have one. Both are
 * token-valued, so neither bypasses `design/tokens/`; the class is the spelling
 * this codebase uses everywhere else a value carries a verdict, `.app-cwv__value--good`
 * included.
 */
function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'success' | 'warning' | 'danger'
}): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__body">
        <div className="ar-muted app-audit__stat-label">{label}</div>
        <div className={`ar-h1 ar-num${tone === undefined ? '' : ` app-audit__stat--${tone}`}`}>
          {value}
        </div>
      </div>
    </div>
  )
}

/**
 * SEO-UI-35…39. The four figures across the top, as the design draws them.
 *
 * **A top-level `ar-grid--4` of cards, not a strip nested in the Summary card.**
 * `seo.html:286-297` puts these above the two finding cards at the same level as
 * them, each one an `ar-card` — surface white, hairline, `--ar-radius-xl`,
 * `--ar-shadow-sm`, `--ar-space-6` of padding — with an `ar-h1 ar-num` figure.
 * The app had them as `.app-stat` tiles inside the Summary card's body: inset
 * grey, no shadow, `ar-h2` at 600 in the body face, and an uppercase label. Four
 * numbers in a sunken strip inside another card read as metadata about that card
 * rather than as the screen's headline, which is the one thing they are.
 *
 * **`.app-stat` is left exactly as it was.** `SearchPerformance.tsx` uses the
 * same class for two strips of its own and has no `seo.html` screen to be in
 * parity with, so restyling the class would have changed an unreviewed screen to
 * fix this one. The markup moved instead.
 *
 * **The tone colours are the design's and they are not decoration** (SEO-UI-38).
 * *AugRank can fix* is `--ar-success`, *Needs you* is `--ar-warning`, *Broken
 * links* is `--ar-danger`, and the health score stays `--ar-text` because it is
 * the only one of the four that is a summary rather than a count of outstanding
 * work. A tenant scanning this row is looking for what is theirs to do, and
 * three identical navy numbers make them read four labels to find it.
 */
function Stats({
  score,
  needsYou,
  canFix,
  brokenLinks,
}: {
  score: number | null
  needsYou: number
  canFix: number
  brokenLinks: number
}): JSX.Element {
  const brand = useBrand()
  return (
    <div className="ar-grid ar-grid--4">
      <Stat label="Health score" value={score === null ? '—' : String(score)} />
      <Stat label={`${brand.productName} can fix`} value={String(canFix)} tone="success" />
      <Stat label="Need you" value={String(needsYou)} tone="warning" />
      <Stat label="Broken links" value={String(brokenLinks)} tone="danger" />
    </div>
  )
}
